import {MD5 as md5} from 'crypto-js';

import {ColorLegend, DiagramNode, NodeLabelColor} from 'src/types';
import {COLOR_EXTRACTED_0, COLOR_EXTRACTED_59} from 'src/styles/colors';
import {SEARCH_NODE} from 'src/constants';
import {hexToRGBComponents, quickContrastingBWColor} from 'src/util/colors';
import {sortAlphabetically} from 'src/util/helpers';

interface nodeTreeDef {
  id?: string | null;
  referencedNodes?: any[] | null;
  containedNodes?: any[] | null;
  groupsNodes?: any[] | null;
  direction?: string | null;
  identity?: string | null;
  name?: string | null;
  type?: string | null;
  containedType?: string | null;
  referencedType?: string | null;
}

/**
 * FlatGraph Spec
 *
 *
 * Input Structure:
 *
 * {
 *    id?
 *    containedNodes[]?
 *    referencedNodes[]?
 *
 * }
 *
 * A contained Node is an object
 * It optionally has an id property
 * It optionally has identity,name, direction property (if it has one of these, it has all of them)
 * It optionally has a containedType property
 * It optionally has a containedNodes property
 * It optionally has a referencedNodes property
 *
 *
 * A referenced Node is an object
 * It optionally has an id property
 * It optionally has identity,name, direction property (if it has one of these, it has all of them)
 * It optionally has a referencedType property
 * It optionally has a containedNodes property
 * It optionally has a referencedNodes property
 *
 *
 *
 * State:
 *   - Parents: a map storing all parents that are currently rendered on screen
 *   - Nodes: a collection of flat objects of currently rendered nodes on screen
 *   - Edges: A collection of <node, node> tuples of relationships of nodes on screen
 *   - NodeTree: Underlying Node Tree Structure (as described above)
 *
 *
 *  Derive from tree:
 *
 *    Inputs: root as NodeTree, id?
 *
 *        Logic:
 *            Precondition: state.NodeTree is defined && id is defined || root is defined
 *
 *
 *
 *            state.nodeTree' = if id and root then
 *                   merge(state.nodeTree, node) for
 *                        node in state.nodeTree where node.id = id & node = merge(root, node)
 *
 *                else if id and not root then
 *                    state.nodeTree where n in nodeTree is-not-in tree where tree.root = id
 *
 *                else if not id and root then
 *                    root
 *
 *
 *             state.Nodes' =
 *                    flatten(state.NodeTree')
 *
 *
 *             state.Edges' =
 *                for all x,y in nodeTree' if y is-a-child-of-x then <x,y> in state.Edges'
 *
 *
 *
 *
 */

export const colorLegend: ColorLegend = {};

/**
 * Build a color lookup function, bound to a callback function
 */
export const makeColorGetter =
  (callbackFn: (key: string, colorObject: NodeLabelColor) => void) =>
    (tag?: string, type = 'node') => {
      if (tag && colorLegend[tag]) {
        return colorLegend[tag];
      }

      const colorObject = colorTag(tag, type);
      if (tag) {
        callbackFn(tag, colorObject);
      }
      return colorObject;
    };

const DEFAULT_COLOR_OBJECT = {
  color: COLOR_EXTRACTED_59,
  fgColor: COLOR_EXTRACTED_0,
  type: 'node',
};

const examineForTagString = (objectToColor?: string | { type?: string }) => {
  if (typeof objectToColor === 'string') {
    return objectToColor;
  } else if (
    objectToColor &&
    objectToColor.type &&
    typeof objectToColor.type === 'string'
  ) {
    return objectToColor.type;
  }

  return undefined;
};

const generateColorForTag = (tagString: string, transparency = false) => {
  const md5hash = md5(tagString);
  let color = '#' + md5hash.toString().substring(0, 6).toUpperCase();
  const [r, g, b] = hexToRGBComponents(color);
  const fgColor = quickContrastingBWColor(r, g, b);

  if (transparency) {
    color = color + '55';
  }

  return {color, fgColor};
};


export function colorTag(objectToColor?: string | { type?: string }, type = 'node', transparency = false) {
  const tagString = examineForTagString(objectToColor);

  if (!tagString) {
    return DEFAULT_COLOR_OBJECT;
  }

  if (colorLegend[tagString]) {
    return colorLegend[tagString];
  }
  const colorObject = {
    ...generateColorForTag(tagString, transparency),
    type,
  };

  return colorLegend[tagString] = colorObject;
}

export type NodeKeyMap = {[k: string]: boolean}

export class FlatGraph {
  nodes: { [key: string]: any }[];
  edges: { [key: string]: any }[];
  tree: NodeTree;
  parents: { [key: string]: any };
  targetId: string | null;
  expand: boolean;
  hiddenNodesMap: NodeKeyMap;

  constructor(tree?: NodeTree | undefined) {
    this.nodes = [];
    this.edges = [];
    this.parents = {};
    this.targetId = 'uninitialized';
    this.hiddenNodesMap = {};
    this.expand = false;

    if (tree) {
      this.tree = tree;
    } else {
      this.tree = new NodeTree();
    }
  }

  public hideNode(idOrKey: string) {
    this.hiddenNodesMap[idOrKey] = true;
  }

  public showNode(idOrKey: string) {
    this.hiddenNodesMap[idOrKey] = false;
  }

  public removeNodefromHiddenList(idOrKey: string) {
    delete this.hiddenNodesMap[idOrKey];
  }

  public isNodeHidden(idOrKey: string) {
    return !!this.hiddenNodesMap[idOrKey];
  }

  public isNodeVisible(idOrKey: string) {
    return !this.isNodeHidden(idOrKey);
  }

  public isNodeTracked(idOrKey: string) {
    return this.hiddenNodesMap.hasOwnProperty(idOrKey);
  }
}

export enum asyncState {
  // eslint-disable-next-line no-unused-vars
  Idle,
  // eslint-disable-next-line no-unused-vars
  Loading,
  // eslint-disable-next-line no-unused-vars
  Loaded,
}

export interface NodeTreeInterface {
  nodes: nodeTreeDef;
  set: (nodes: nodeTreeDef) => void;
  initialize: () => Promise<void>;
  state: asyncState;
  search: (id: string, cb: ((any) => any) | null) => nodeTreeDef;
}

export interface TopLevel {
  nodesResponse: any,
  groupsResponse: any
}

export class NodeTree implements NodeTreeInterface {
  nodes: nodeTreeDef;
  state: asyncState;

  constructor(nodes?: nodeTreeDef | null, cb?: () => Promise<TopLevel> | undefined) {
    if (nodes) {
      this.nodes = nodes;
      this.state = asyncState.Loaded;
    } else {
      this.nodes = {
        containedNodes: [],
        referencedNodes: [],
      };
      this.state = asyncState.Loading;
      this.initialize(cb);
    }
  }

  public traverse(
      cb1: (...any) => any,
      cb2: (...any) => any,
      cb3: (...any) => any,
  ) {
  }

  public async initialize(cb?: () => Promise<TopLevel> | undefined): Promise<void> {
    try {
      if (cb) {
        const res = await cb();

        const containedNodes = res?.nodesResponse.data.containedNodes || [];
        const referencedNodes = res?.nodesResponse.data.referencedNodes || [];
        const groupNodes = res?.groupsResponse.data;

        for (let i = 0; i < containedNodes.length; i++) {
          containedNodes[i].containedNodes =
            containedNodes[i].containedNodes.sort(sortAlphabetically);
        }
        for (let i = 0; i < referencedNodes.length; i++) {
          referencedNodes[i].referencedNodes =
            referencedNodes[i].referencedNodes.sort(sortAlphabetically);
        }

        groupNodes.forEach((n) => {
          n.relationshipCount = n.groupedNodeCount + n.childGroupingCount;
          n.isGroup = true;
        });
        groupNodes.sort(sortAlphabetically);

        this.nodes = {...res?.nodesResponse.data, containedNodes, referencedNodes, groupNodes};
      }

      this.state = asyncState.Loaded;
    } catch (e) {
      this.state = asyncState.Loaded;
      throw e;
    }
  }

  public set(tree: nodeTreeDef): void {
    this.nodes = tree;
  }

  public search(id: string, cb: ((any) => any) | null) {
    const searchClosure = (root, id) => {
      // Check if leaf node as base case or if gone down non-existent path (for type specific node)
      if (!root) {
        return;
      }

      if (root.id === id) {
        if (cb) {
          cb(root);
        }
        return root;
      }

      const contained = root.containedNodes || null;
      const referenced = root.referencedNodes || null;

      contained &&
      contained.forEach((n) => {
        searchClosure(n, id);
      });

      referenced &&
      referenced.forEach((n) => {
        searchClosure(n, id);
      });
    };

    return searchClosure(this.nodes, id);
  }
}

export const isSearchNode = (type: string | undefined) => {
  return type === SEARCH_NODE;
};

/**
 * @deprecated
 * Interim local type for NodeData, may not be 100% accurate since it relies on assumptions
 * based on how the un-typed code is used here.
 */
type NodeData = {
  id: string,
  type?: string,
  identity: string,
  name: string,
  relationshipCount?: number,
  properties?: {
    shortName?: string
    primaryLabel?: string
  }
}

export const mapContainedNode = (
    {
      id,
      identity,
      name,
      properties,
      relationshipCount,
    }: NodeData,
    type?: string,
    getColorTag?: typeof colorTag,
) => ({
  metadata: {
    type,
    identity,
  },
  text: name || identity,
  properties,
  relationshipCount,
  shortName: properties?.shortName,
  color: getColorTag ? getColorTag(type, undefined, isSearchNode(type)).color : colorTag(type, undefined, isSearchNode(type)).color,
  key: id,
  id,
});

/**
 * @deprecated
 * Type signature generated based on the assumptions the code
 * in this method is making.  It may be inaccurate and should be
 * replaced with correct types
 */
type GroupData = NodeData & {groupedNodeCount: number, childGroupingCount: number}

export const mapGroupData = (groups: Array<GroupData>) => {
  if (groups) {
    return groups.map((n) => {
      const result: DiagramNode = mapContainedNode(n, n.type);
      result.isGroupNode = true;
      if (n.groupedNodeCount > 0 || n.childGroupingCount > 0) {
        result.category = 'parent';
      }
      return result;
    });
  } else {
    return [];
  }
};

export const mapContainedNodesToNodes = (
    containedNodes: Array<any>,
    getColorTag?: typeof colorTag,
) => {
  const nodes: Array<any> = [];
  containedNodes.forEach((cn) => {
    cn.containedNodes.forEach((n) => {
      const result: any = mapContainedNode(n, cn.containedType, getColorTag);
      result.isGroupNode = false;
      if (n.relationshipCount > 0) {
        result.category = 'parent';
      }
      nodes.push(result);
    });
  });
  return nodes;
};

export const mapContainedNodesToSearchNodes = (containedNodes: Array<any>) => {
  const nodes: Array<any> = [];
  containedNodes.forEach((cn) => {
    cn.containedNodes.forEach((n) => {
      const result: any = {...n, containedType: cn.containedType};
      result.isGroupNode = false;
      if (n.relationshipCount > 0) {
        result.category = 'parent';
      }
      nodes.push(result);
    });
  });
  return nodes;
};

export const mapReferencedNodesToNodes = (
    referencedNodes: Array<any>,
    getColorTag?: typeof colorTag,
) => {
  const nodes: Array<any> = [];
  referencedNodes.forEach((cn) => {
    cn.referencedNodes.forEach((n) => {
      const result: any = {};
      result.metadata = {};
      result.metadata.type = cn.referencedType;
      result.metadata.identity = n.identity;
      result.text = n.name || n.identity;
      result.shortName = n.properties?.shortName;
      result.color = getColorTag ? getColorTag(cn.referencedType).color : colorTag(cn.referencedType).color;
      result.key = n.id;
      result.id = n.id;
      result.isGroupNode = false;
      result.relationshipCount = n.relationshipCount;
      result.properties = n.properties;
      if (n.relationshipCount > 0) {
        result.category = 'parent';
      }
      nodes.push(result);
    });
  });
  return nodes;
};

export const mapContainedNodesToEdges = (
    containedNodes: Array<any>,
    baseNodeId: string,
    getColorTag?: typeof colorTag,
) => {
  const edges: Array<any> = [];

  containedNodes.forEach((cn) => {
    cn.containedNodes.forEach((n) => {
      const result: any = {};
      result.from = baseNodeId;
      result.to = n.id;
      result.key = 'con.' + n.id + baseNodeId;
      result.color = getColorTag ? getColorTag('*' + n.relationshipLabels[0], 'edge').color : colorTag('*' + n.relationshipLabels[0], 'edge').color;
      result.contains = true;
      result.destType = cn.containedType;
      result.diffValue = n.relationshipDiffValue;
      result.type = n.relationshipLabels[0];
      edges.push(result);
    });
  });
  return edges;
};

export const mapReferencedNodesToEdges = (
    referencedNodes: Array<any>,
    baseNodeId: string,
    getColorTag?: typeof colorTag,
) => {
  const edges: Array<any> = [];

  referencedNodes.forEach((cn) => {
    cn.referencedNodes.forEach((n) => {
      const result: any = {};
      result.from = baseNodeId;
      result.to = n.id;
      result.key = 'ref.' + n.id + baseNodeId;
      result.color = getColorTag ? getColorTag('*' + n.relationshipLabels[0], 'edge').color : colorTag('*' + n.relationshipLabels[0], 'edge').color;
      result.contains = false;
      result.destType = cn.referencedType;
      result.diffValue = n.relationshipDiffValue;
      result.type = n.relationshipLabels[0];
      edges.push(result);
    });
  });
  return edges;
};
